"use client"

import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Header } from "../components/Header"
import { BottomNavigation } from "../components/BottomNavigation"
import { useServicePalette } from "../hooks/useServicePalette"
import { ProductCard } from "../components/ProductCard"
import { OfferCard } from "../components/OfferCard"
import { LoadingSpinner } from "../components/LoadingSpinner"
import { useAuthStore } from "../stores/useAuthStore"
import { useCartStore } from "../stores/useCartStore"
import { useCurrencySymbol } from "../lib/currency"
import { apiClient } from "../lib/api"
import type { Product, Menu, Offer } from "../types"
import { Search, Sparkles, Clock } from "lucide-react"
import { isRestaurantOpen } from "../lib/date"
import type { TenantConfig } from "../types"
import { ProductDetailModal } from "../components/ProductDetailModal"

export function MenuPage() {
  const { tableId } = useParams<{ tableId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { getTotal, getItemCount, addItem } = useCartStore()
  const currencySymbol = useCurrencySymbol()
  const [menus, setMenus] = useState<Menu[]>([])
  const [offers, setOffers] = useState<Offer[]>([])
  const [activeCategory, setActiveCategory] = useState<string>("all")
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(true)
  const [config, setConfig] = useState<TenantConfig | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/loading/${tableId}`)
      return
    }

    loadData()
  }, [isAuthenticated, tableId, navigate])

  const loadData = async () => {
    try {
      setLoading(true)

      const [offersData, menusData, configData] = await Promise.all([
        // apiClient.getProducts(), // We don't need to fetch all products anymore, we use the ones in the menus
        apiClient.getOffers(),
        apiClient.getFilteredMenus(),
        apiClient.getConfig(),
      ])

      const tenantConfig = (configData as any).config as TenantConfig;
      const restaurantOpen = isRestaurantOpen(tenantConfig);
      setIsOpen(restaurantOpen);
      setConfig(tenantConfig)

      // If closed and NOT in scheduled mode, redirect after a short delay or show message
      // For now, we'll just set the state and let the UI handle it (disabling buttons via a global message)

      // setProducts(productsData as Product[]) // We don't use this global list anymore
      setOffers(offersData as Offer[])
      setMenus(menusData as Menu[])
    } catch (error) {
      console.error("Error loading menu data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCallWaiter = async () => {
    // Implementation for calling waiter
  }

  // Filter products based on search query and active category
  const getFilteredProducts = () => {
    let candidates: Product[] = [];

    if (activeCategory === "all") {
      // Combine all products from all available menus, deduplicating by UUID
      const allProductsMap = new Map<string, Product>();
      menus.forEach(menu => {
        // Ensure menu.products is an array before iterating
        if (Array.isArray(menu.products)) {
          menu.products.forEach(product => {
            allProductsMap.set(product.uuid, product);
          });
        }
      });
      candidates = Array.from(allProductsMap.values());
    } else if (activeCategory === "offers") {
      return []; // Handled separately
    } else {
      // Specific menu
      const activeMenu = menus.find(menu => menu.id.toString() === activeCategory);
      if (activeMenu && Array.isArray(activeMenu.products)) {
        candidates = activeMenu.products;
      }
    }

    // Apply search filter
    return candidates.filter(
      (product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  const filteredProducts = getFilteredProducts()

  const filteredOffers = offers.filter(
    (offer) =>
      offer.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      offer.price !== 0.0, // Exclude zero-price offers
  )
  const cartTotal = getTotal()
  const cartItemCount = getItemCount()
  const palette = useServicePalette();

  if (loading) {
    if (!config?.accept_scheduled_orders && !isOpen) { // Added !isOpen to ensure this only shows when closed AND no scheduled orders
      return (
        <div className="mx-4 p-8 bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800 rounded-3xl text-center space-y-4 animate-in fade-in zoom-in duration-500">
          <div className="w-16 h-16 bg-rose-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg text-white">
            <Clock className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-black text-rose-900 dark:text-rose-100">Pedidos programados no disponibles</h3>
            <p className="text-sm text-rose-700 dark:text-rose-300 font-medium">En este momento el restaurante no está aceptando pedidos programados.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageTo} dark:from-gray-900 dark:to-gray-800`}>
        <Header title="Menú" showBack showCart />
        <LoadingSpinner message="Cargando menú..." />
        <BottomNavigation tableId={tableId || ""} onOpenChatbot={handleCallWaiter} />
      </div>
    )
  }

  // Check if a menu is valid for the current DATE (ignoring time) to decide visibility
  const isMenuDateValid = (menu: Menu): boolean => {
    if (!menu.schedule) return true;
    const dayMap = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const now = new Date();
    const todayKey = dayMap[now.getDay()];
    const todaySchedule = menu.schedule[todayKey] || menu.schedule[todayKey.normalize("NFD").replace(/[\u0300-\u036f]/g, "")];

    if (!todaySchedule) return false;

    // Check Date Range if exists
    if (typeof todaySchedule === 'object' && todaySchedule !== null) {
      const ts = todaySchedule as any;
      if (ts.date) {
        const todayStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
        if (ts.date.start && todayStr < ts.date.start) return false;
        if (ts.date.end && todayStr > ts.date.end) return false;
      }
    }
    return true;
  };

  const visibleMenus = menus.filter(isMenuDateValid);

  // Helper to check if a menu is currently valid based on its schedule (Time)
  const isMenuTimeValid = (menu: Menu): boolean => {
    if (!menu.schedule) return true;

    const dayMap = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const now = new Date();
    const todayKey = dayMap[now.getDay()];
    const todaySchedule = menu.schedule[todayKey] || menu.schedule[todayKey.normalize("NFD").replace(/[\u0300-\u036f]/g, "")];

    if (!todaySchedule) return false;

    // We assume Date validity is checked by isMenuDateValid (which controls visibility/filtering)
    // But we check checks again just in case, or we implicitly trust visibleMenus?
    // Let's keep logic robust.

    let start = "";
    let end = "";

    if (typeof todaySchedule === 'string') {
      const parts = todaySchedule.split('-');
      if (parts.length === 2) {
        start = parts[0];
        end = parts[1];
      }
    } else if (typeof todaySchedule === 'object' && todaySchedule !== null) {
      const ts = todaySchedule as any;
      if (ts.time) {
        start = ts.time.start;
        end = ts.time.end;
      } else if (ts.start && ts.end) {
        start = ts.start;
        end = ts.end;
      }
    }

    if (!start || !end) return true; // Empty start/end means All Day -> Valid

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const startTime = startH * 60 + startM;
    let endTime = endH * 60 + endM;

    if (endTime < startTime) {
      return currentTime >= startTime || currentTime <= endTime;
    }

    return currentTime >= startTime && currentTime <= endTime;
  };

  // Check if a product is orderable (belongs to at least one currently valid menu)
  const isProductOrderable = (productUuid: string): boolean => {
    // Find all menus that contain this product
    // Only consider visible menus? Yes, if it's hidden by date, you shouldn't be able to order.
    const productMenus = visibleMenus.filter(m => m.products.some(p => p.uuid === productUuid));
    return productMenus.some(isMenuTimeValid);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32`}>
      <Header title="Menú" showBack showCart showServiceIndicator={true} />

      {!isOpen && (
        <div className={`border-b p-4 flex items-start gap-4 sticky top-0 z-50 backdrop-blur-md animate-in fade-in slide-in-from-top-2 duration-300 ${config?.accept_scheduled_orders ? 'bg-amber-50/95 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' : 'bg-rose-50/95 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800'}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${config?.accept_scheduled_orders ? 'bg-amber-500 text-white' : 'bg-rose-500 text-white'}`}>
            <Clock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className={`font-bold text-sm ${config?.accept_scheduled_orders ? 'text-amber-900 dark:text-amber-100' : 'text-rose-900 dark:text-rose-100'}`}>
              {config?.accept_scheduled_orders ? 'Restaurante fuera de horario' : 'Restaurante Cerrado'}
            </p>
            <p className={`text-xs font-medium leading-relaxed ${config?.accept_scheduled_orders ? 'text-amber-700 dark:text-amber-300' : 'text-rose-700 dark:text-rose-300'}`}>
              {config?.accept_scheduled_orders
                ? 'Solo podés navegar el menú. Para pedir, usá la opción de Pedidos Programados.'
                : 'En este momento no estamos tomando pedidos. Por favor, consulta nuestros horarios en el inicio.'}
            </p>
          </div>
          <button
            onClick={() => navigate(`/dashboard/${tableId}`)}
            className={`text-xs font-black uppercase tracking-wider py-2 px-3 rounded-lg border transition-all duration-200 ${config?.accept_scheduled_orders ? 'border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300' : 'border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-700 dark:text-rose-300'}`}
          >
            Volver
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-6 shadow-sm border-b border-gray-100 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar platos, bebidas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-6 shadow-sm">
        <div className="flex gap-3 overflow-x-auto pb-2">
          {offers.length > 0 && (
            <button
              onClick={() => setActiveCategory("offers")}
              className={`px-6 py-3 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 shadow-sm flex items-center gap-2 ${activeCategory === "offers"
                ? `bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} text-white shadow-lg scale-105`
                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-sky-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                }`}
            >
              <Sparkles className="w-4 h-4" />
              Ofertas
            </button>
          )}
          {visibleMenus.map((menu) => (
            <button
              key={menu.id}
              onClick={() => setActiveCategory(menu.id.toString())}
              className={`px-6 py-3 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 shadow-sm ${activeCategory === menu.id.toString()
                ? `bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} text-white shadow-lg scale-105`
                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-sky-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                }`}
            >
              {menu.name}
            </button>
          ))}
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-6 py-3 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 shadow-sm ${activeCategory === "all"
              ? `bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} text-white shadow-lg scale-105`
              : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-sky-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
              }`}
          >
            Todos
          </button>
        </div>
      </div>

      {/* Menu Schedule Info */}
      {(() => {
        if (activeCategory === "all" || activeCategory === "offers") return null;
        const activeMenu = visibleMenus.find(m => m.id.toString() === activeCategory);
        if (!activeMenu) return null;

        // Check validity to style the banner
        const isValid = isMenuTimeValid(activeMenu);
        const bannerColor = isValid
          ? "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200"
          : "bg-rose-50 dark:bg-rose-900/10 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-200";
        const iconColor = isValid ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400";

        if (!activeMenu?.schedule) return null;

        // Map JS getDay() (0=Sun, 1=Mon...) to schedule keys
        const dayMap = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
        const todayKey = dayMap[new Date().getDay()];
        const todaySchedule = activeMenu.schedule[todayKey] || activeMenu.schedule[todayKey.normalize("NFD").replace(/[\u0300-\u036f]/g, "")];

        if (!todaySchedule) return null;

        let scheduleText = "";

        if (typeof todaySchedule === 'string') {
          scheduleText = todaySchedule.replace('-', ' a ');
        } else if (typeof todaySchedule === 'object' && todaySchedule !== null) {
          const ts = todaySchedule as any;
          if (ts.time) {
            if (ts.time.start && ts.time.end) {
              scheduleText = `${ts.time.start} a ${ts.time.end}`;
            } else {
              scheduleText = "Todo el día";
            }
          } else if (ts.start && ts.end) {
            scheduleText = `${ts.start} a ${ts.end}`;
          } else {
            scheduleText = "Todo el día";
          }
        }

        if (scheduleText === "Todo el día" && isValid) return null;

        return (
          <div className={`mx-6 mt-4 p-3 border rounded-xl flex items-center justify-center gap-2 animate-in fade-in slide-in-from-top-2 ${bannerColor}`}>
            <Clock className={`w-4 h-4 ${iconColor}`} />
            <p className="text-sm font-medium">
              {isValid
                ? (scheduleText === "Todo el día" ? "Menú válido todo el día" : `Menú válido de ${scheduleText}`)
                : `Menú cerrado (${scheduleText === "Todo el día" ? "Cerrado por fecha" : `Horario: ${scheduleText}`})`}
            </p>
          </div>
        );
      })()}

      {/* Products Grid */}
      <div className="p-6">
        {searchQuery && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Resultados para "{searchQuery}"</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {(activeCategory === "offers" ? filteredOffers : filteredProducts).length} resultados encontrados
            </p>
          </div>
        )}

        <div className="grid gap-6">
          {activeCategory === "offers" ? (
            filteredOffers.length > 0 ? (
              filteredOffers.map((offer) => <OfferCard key={offer.uuid} offer={offer} />)
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No hay ofertas disponibles</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchQuery
                    ? "Intenta con otros términos de búsqueda"
                    : "Vuelve pronto para ver nuestras ofertas especiales"}
                </p>
              </div>
            )
          ) : filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <ProductCard
                key={product.uuid}
                product={product}
                disabled={!isProductOrderable(product.uuid)}
                disabledMessage="Fuera de horario"
                onOpenDetail={setSelectedProduct}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No se encontraron productos</h3>
              <p className="text-gray-600 dark:text-gray-400">Intenta con otros términos de búsqueda</p>
            </div>
          )}
        </div>
      </div>

      {/* Cart Footer */}
      {cartItemCount > 0 && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-40">
          <button
            onClick={() => navigate(`/cart/${tableId}`)}
            className={`w-full bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} hover:${palette.primaryFromHover} hover:${palette.primaryToHover} backdrop-blur-sm text-white py-5 rounded-2xl font-bold transition-all duration-200 flex items-center justify-between px-8 shadow-2xl border border-white/20 hover:scale-105`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold">{cartItemCount}</span>
              </div>
              <span className="text-sm font-semibold">{cartItemCount === 1 ? "Item" : "Items"}</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-lg font-bold">Ver Carrito</span>
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            </div>

            <div className="text-right">
              <div className="text-xs opacity-80">Total</div>
              <div className="text-lg font-bold">{currencySymbol}{cartTotal.toFixed(2)}</div>
            </div>
          </button>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNavigation tableId={tableId || ""} onOpenChatbot={handleCallWaiter} />

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAdd={(prod, qty, opts) => addItem(prod, qty, opts)}
          currencySymbol={currencySymbol}
        />
      )}
    </div>
  )
}
